'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Loader2, CalendarX, Globe, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTimezoneAbbr, convertTimeToTimezone } from '@/lib/utils/timezone';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AvailableTimeSlot {
  date: string;
  start_time: string;
  end_time: string;
}

interface DateSlotPickerProps {
  handle: string;
  durationMinutes: number;
  onSelect: (date: string, startTime: string, endTime: string, guideTimezone: string, guestTimezone: string) => void;
  selectedDate?: string;
  selectedTime?: string;
}

function getTimezoneFriendlyName(tz: string): string {
  const city = tz.split('/').pop()?.replace(/_/g, ' ') || tz;
  const abbr = getTimezoneAbbr(tz);
  if (abbr !== city) return `${abbr} - ${city}`;
  return city;
}

function getCurrentTimeInTz(tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date());
}

export default function DateSlotPicker({
  handle,
  durationMinutes,
  onSelect,
  selectedDate,
  selectedTime,
}: DateSlotPickerProps) {
  const [slots, setSlots] = useState<AvailableTimeSlot[]>([]);
  const [guideTimezone, setGuideTimezone] = useState('America/New_York');
  const [loading, setLoading] = useState(true);
  const [calendarDate, setCalendarDate] = useState<Date | undefined>(
    selectedDate ? new Date(selectedDate + 'T12:00:00') : undefined
  );
  const [displayTimezone, setDisplayTimezone] = useState<'guest' | 'guide'>('guest');

  const browserTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const effectiveDisplayTz = displayTimezone === 'guest' ? browserTimezone : guideTimezone;
  const showConverted = effectiveDisplayTz !== guideTimezone;

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    const now = new Date();
    const start = now.toISOString().substring(0, 10);
    const end = new Date(now.getTime() + 28 * 24 * 60 * 60 * 1000)
      .toISOString()
      .substring(0, 10);

    try {
      const res = await fetch(
        `/api/guide/availability/${handle}?start=${start}&end=${end}&duration=${durationMinutes}`
      );
      const data = await res.json();
      setSlots(data.slots || []);
      if (data.timezone) {
        setGuideTimezone(data.timezone);
        if (data.timezone === browserTimezone) {
          setDisplayTimezone('guide');
        }
      }
    } catch {
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [handle, durationMinutes, browserTimezone]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const availableDates = new Set(slots.map((s) => s.date));

  const timesForDate = calendarDate
    ? slots.filter(
        (s) => s.date === calendarDate.toISOString().substring(0, 10)
      )
    : [];

  const currentDateStr = calendarDate?.toISOString().substring(0, 10);

  function formatTime(t: string) {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="flex flex-col items-center py-8 text-center">
        <CalendarX className="w-8 h-8 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">
          No available times in the next 4 weeks. Check back later.
        </p>
      </div>
    );
  }

  const sameTimezone = browserTimezone === guideTimezone;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">Select a date</p>
        <Calendar
          mode="single"
          selected={calendarDate}
          onSelect={(date) => {
            if (date) setCalendarDate(date);
          }}
          disabled={(date) => {
            const dateStr = date.toISOString().substring(0, 10);
            return !availableDates.has(dateStr);
          }}
          fromDate={new Date()}
          toDate={new Date(Date.now() + 28 * 24 * 60 * 60 * 1000)}
        />
      </div>

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Globe className="w-3.5 h-3.5 flex-shrink-0" />
        {sameTimezone ? (
          <span>{getTimezoneFriendlyName(guideTimezone)} ({getCurrentTimeInTz(guideTimezone)})</span>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 hover:text-foreground transition-colors">
              <span>{getTimezoneFriendlyName(effectiveDisplayTz)} ({getCurrentTimeInTz(effectiveDisplayTz)})</span>
              <ChevronDown className="w-3 h-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => setDisplayTimezone('guest')}>
                <div className="flex flex-col">
                  <span className={cn(displayTimezone === 'guest' && 'font-medium')}>
                    {getTimezoneFriendlyName(browserTimezone)}
                  </span>
                  <span className="text-xs text-muted-foreground">Your timezone</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDisplayTimezone('guide')}>
                <div className="flex flex-col">
                  <span className={cn(displayTimezone === 'guide' && 'font-medium')}>
                    {getTimezoneFriendlyName(guideTimezone)}
                  </span>
                  <span className="text-xs text-muted-foreground">Guide&apos;s timezone</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {currentDateStr && timesForDate.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Available times</p>
          <div className="grid grid-cols-3 gap-2">
            {timesForDate.map((slot) => {
              const displayTime = showConverted
                ? convertTimeToTimezone(slot.start_time, currentDateStr, guideTimezone, effectiveDisplayTz)
                : formatTime(slot.start_time);

              return (
                <Button
                  key={slot.start_time}
                  variant="outline"
                  size="sm"
                  className={cn(
                    'text-xs h-auto py-1.5',
                    selectedDate === currentDateStr &&
                      selectedTime === slot.start_time &&
                      'border-primary bg-primary/5 text-primary'
                  )}
                  onClick={() =>
                    onSelect(currentDateStr, slot.start_time, slot.end_time, guideTimezone, browserTimezone)
                  }
                >
                  {displayTime}
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
